import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  QUERIES,
  getAccessToken,
  removeAccessToken,
  Loading,
  currentUser,
  ICurrentUser,
  removeRefreshToken,
  openNotification,
  INotificationResponse,
  BASE_URL,
  NotificationType,
  Authority,
} from "@dataone/lib";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import SockJS from "sockjs-client";
import Stomp from "stompjs";

interface ContextType {
  user: ICurrentUser | null;
  isCurrentUserLoad: boolean;
  isAuthenticated: boolean;
  notifications: INotificationResponse[];
  getCurrentUser: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult<ICurrentUser>>;
  setUser: React.Dispatch<React.SetStateAction<ICurrentUser | null>>;
  logout: () => void;
}

const AuthContext = createContext<ContextType>({
  user: null,
  notifications: [],
  isCurrentUserLoad: false,
  isAuthenticated: false,
  setUser: () => {},
  getCurrentUser: () => Promise.resolve({} as UseQueryResult<ICurrentUser>),
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<ICurrentUser | null>(null);
  const [isAuthenticated, setAuthenticated] = useState(!!getAccessToken());

  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [notifications, setNotifications] = useState<INotificationResponse[]>(
    []
  );

  const navigate = (path: string) => {
    window.location.href = path;
  };

  const { isLoading: isCurrentUserLoad, refetch: getCurrentUser } = useQuery(
    [QUERIES.USER_INFO],
    currentUser,
    {
      enabled: isAuthenticated && !user,
      onSuccess: (user) => {
        setUser(user);
        setAuthenticated(true);
      },
      onError: () => {
        socket?.close();
        removeAccessToken();
        removeRefreshToken();
        // setAuthenticated(false);
        navigate("/auth/sign-in?sessionExpired=true");
      },
    }
  );

  useEffect(() => {
    const initializeWebSocket = async (userId: string) => {
      try {
        const sock = new SockJS(`${BASE_URL}/notification/ws`);
        const stompClient = Stomp.over(sock);

        stompClient.connect({}, () => {
          stompClient.subscribe(`/user/${userId}/queue`, (response) => {
            const notification = JSON.parse(
              response.body
            ) as INotificationResponse;
            if (notification.type === NotificationType.TOPIC)
              openNotification(
                `New topics have been generated for ${notification.displayName} data source`,
                "info"
              );
            else openNotification(notification.data, "info");
            if (
              [
                NotificationType.SUBSCRIPTION_CREATED,
                NotificationType.PAYMENT_FAILED,
              ].includes(notification.type)
            )
              getCurrentUser();
            setNotifications((prevNotifications) => [
              ...prevNotifications,
              notification,
            ]);
          });
        });

        setSocket(sock);
      } catch (error) {
        console.error("Error initializing WebSocket:", error);
      }
    };

    user && !socket && initializeWebSocket(user.userId);

    return () => {
      // if (socket) socket.close();
    };
  }, [socket, user]);

  useEffect(() => {
    if (!isAuthenticated && !isCurrentUserLoad) {
      socket?.close();
      setAuthenticated(false);
      removeAccessToken();
      removeRefreshToken();
      // if (!pathname.startsWith("/auth"))
      // navigate("/auth/sign-in");
    }
  }, [isAuthenticated]);

  const logout = () => {
    // setUser(null);
    socket?.close();
    setAuthenticated(false);
    removeAccessToken();
    removeRefreshToken();
    // navigate("/auth/sign-in");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isCurrentUserLoad,
        isAuthenticated,
        notifications,
        setUser,
        getCurrentUser,
        logout,
      }}
    >
      {isCurrentUserLoad && isAuthenticated && !user ? <Loading /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
